// Margins.jsx — Real profit margin analysis.
//
// What this tab answers:
//   1) Per-product margin: revenue (from product report) − cost (from cost file or
//      product report's `profit` column if cost file missing) → margin %.
//   2) Overall profitability: total revenue − total product cost − total purchases
//      spend (operating cost) → real net profit for the period.
//   3) Star products / loss-makers / margin distribution.
//
// Inputs:
//   - prods: aggregated product report (name, rev, qty, profit, disc)
//   - costs: optional cost file (name, cost per unit)
//   - purch: optional purchases data (operating cost from suppliers)
//
// Cost resolution priority for each product:
//   a) Cost file's matching row (preferred — user-supplied accurate cost)
//   b) Implied cost from Retm's `profit` column: cost_per_unit = (rev − profit) / qty
//   c) If neither available, product is shown but excluded from margin aggregates.

import React, { useMemo, useEffect, useState } from "react";
import { Kpi } from "./Dashboard";
import { mkC } from "./lib/chartFactory";
import { downloadCSV } from "./lib/export";
import { api, formatErr } from "./lib/api";
import { formatKSA } from "./lib/ksaTime";

const fm  = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");
const fm2 = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// Saudi VAT rate. Product cost fields are entered VAT-EXCLUSIVE (what we
// actually pay for inputs), while POS revenue is VAT-INCLUSIVE (what the
// customer pays at the till). To compute a real margin we must strip VAT
// from revenue before subtracting cost. Centralised here for easy tuning
// if the rate changes (e.g. new fiscal year regulation).
export const VAT_RATE = 0.15;
const stripVAT = (gross) => (Number(gross) || 0) / (1 + VAT_RATE);

function buildMargins(prods, costs) {
  const costMap = new Map((costs || []).map((c) => [(c.name || "").trim().toLowerCase(), Number(c.cost) || 0]));
  return (prods || []).map((p) => {
    const key = (p.name || "").trim().toLowerCase();
    const fileCostPerUnit = costMap.get(key);
    // Net revenue (VAT removed). POS reports gross prices; subtracting a
    // VAT-exclusive cost from a VAT-inclusive revenue inflates margins by
    // ~13% (1.15× factor), so we always strip the 15% first.
    const netRev = stripVAT(p.rev);
    // Implied cost from RETM `profit` column already uses gross prices, so
    // we keep that path on the gross side; only file-based costs need VAT
    // adjustment on the revenue.
    const impliedCostPerUnit = p.qty > 0 ? (p.rev - p.profit) / p.qty : 0;
    const usingFileCost = fileCostPerUnit !== undefined;
    const costPerUnit = usingFileCost ? fileCostPerUnit : impliedCostPerUnit;
    const totalCost = costPerUnit * p.qty;
    const baseRev = usingFileCost ? netRev : p.rev;
    const marginAbs = baseRev - totalCost;
    const marginPct = baseRev > 0 ? (marginAbs / baseRev * 100) : 0;
    return {
      ...p,
      costPerUnit,
      totalCost,
      netRev,
      marginAbs,
      marginPct,
      _costSource: usingFileCost ? "file" : (p.profit !== 0 ? "retm" : "none"),
    };
  });
}

export default function Margins({ prods, costs, purch, onUploadProds, onUploadCosts, canUpload }) {
  const hasProds = (prods || []).length > 0;
  const enriched = useMemo(() => buildMargins(prods, costs), [prods, costs]);
  const validMargins = useMemo(() => enriched.filter((p) => p.rev > 0 && p._costSource !== "none"), [enriched]);

  // AI insights state
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAt, setAiAt] = useState(null);
  const [aiErr, setAiErr] = useState(null);

  const runAi = async () => {
    setAiBusy(true); setAiErr(null);
    try {
      const { data } = await api.post("/ai/profit-insights");
      setAiText(data.text || "");
      setAiAt(data.generated_at || new Date().toISOString());
    } catch (e) {
      setAiErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setAiBusy(false); }
  };

  const stats = useMemo(() => {
    // Gross revenue = what was rung up at POS (VAT-inclusive). Kept for
    // till-level transparency only — never used in profit math.
    const totalRevGross = enriched.reduce((s, p) => s + p.rev, 0);
    // Net revenue base used in profit math. For file-cost rows we strip
    // VAT (15%); for RETM-implied cost rows the profit column already
    // matches gross prices, so we keep gross there to stay consistent
    // with the per-row marginAbs.
    const totalRevBase = validMargins.reduce(
      (s, p) => s + (p._costSource === "file" ? p.netRev : p.rev),
      0,
    );
    const totalCost   = validMargins.reduce((s, p) => s + p.totalCost, 0);
    // Use per-row marginAbs (already VAT-correct) — DO NOT recompute as
    // (gross - cost), that mixes VAT-inclusive revenue with VAT-exclusive
    // cost and inflates profit by ~15%. This was the v118.11 bug.
    const totalProfit  = validMargins.reduce((s, p) => s + p.marginAbs, 0);
    const avgMarginPct = totalRevBase > 0 ? (totalProfit / totalRevBase * 100) : 0;
    // Approximate VAT on the file-cost portion (informational only)
    const vatOnSales   = validMargins
      .filter((p) => p._costSource === "file")
      .reduce((s, p) => s + (p.rev - p.netRev), 0);
    const lossMakers = validMargins.filter((p) => p.marginAbs < 0).sort((a, b) => a.marginAbs - b.marginAbs);
    const stars = [...validMargins].filter((p) => p.rev > 200).sort((a, b) => b.marginPct - a.marginPct).slice(0, 10);
    const worst = [...validMargins].filter((p) => p.rev > 200).sort((a, b) => a.marginPct - b.marginPct).slice(0, 10);
    // Purchases total spend (only count closed invoices). Assumed
    // VAT-inclusive (typical supplier invoice). Stripped to net before
    // subtracting from net product profit to stay on the same basis.
    const purchTotal = (purch || []).filter((p) => !p.draft).reduce((s, p) => s + (p.total || 0), 0);
    const purchNet   = purchTotal / (1 + VAT_RATE);
    const realNet    = totalProfit - purchNet;
    const costFromFile = enriched.filter((p) => p._costSource === "file").length;
    const costFromRetm = enriched.filter((p) => p._costSource === "retm").length;
    const costMissing  = enriched.filter((p) => p._costSource === "none").length;
    return {
      totalRev: totalRevGross,        // kept for back-compat with other readers
      totalRevGross, totalRevBase, vatOnSales,
      totalCost, totalProfit, avgMarginPct, lossMakers, stars, worst,
      purchTotal, purchNet, realNet,
      costFromFile, costFromRetm, costMissing,
    };
  }, [enriched, validMargins, purch]);

  // Build charts
  useEffect(() => {
    if (!validMargins.length) return;
    // Top 10 margin %
    mkC("c-marg-top", "bar", stats.stars.map((p) => p.name.slice(0, 14)), [{
      label: "هامش %",
      data: stats.stars.map((p) => +p.marginPct.toFixed(1)),
      backgroundColor: "rgba(16,185,129,.8)",
      borderRadius: 4,
    }], { indexAxis: "y", scales: { x: { max: 100, ticks: { callback: (v) => v + "%" } } } });
    // Worst 10
    mkC("c-marg-worst", "bar", stats.worst.map((p) => p.name.slice(0, 14)), [{
      label: "هامش %",
      data: stats.worst.map((p) => +p.marginPct.toFixed(1)),
      backgroundColor: stats.worst.map((p) => p.marginPct < 0 ? "rgba(220,38,38,.85)" : "rgba(217,119,6,.8)"),
      borderRadius: 4,
    }], { indexAxis: "y", scales: { x: { ticks: { callback: (v) => v + "%" } } } });
  });

  const exportMarginsCsv = () => {
    const headers = ["#", "المنتج", "الكمية", "الإيراد (صافي)", "تكلفة/وحدة", "إجمالي التكلفة", "هامش", "هامش %", "مصدر التكلفة"];
    const rows = [...enriched].sort((a, b) => b.rev - a.rev).map((p, i) => {
      const baseRev = p._costSource === "file" ? p.netRev : p.rev;
      return [
        i + 1, p.name, p.qty, baseRev.toFixed(2),
        p._costSource === "none" ? "—" : p.costPerUnit.toFixed(2),
        p._costSource === "none" ? "—" : p.totalCost.toFixed(2),
        p._costSource === "none" ? "—" : p.marginAbs.toFixed(2),
        p._costSource === "none" ? "—" : p.marginPct.toFixed(1) + "%",
        p._costSource === "file" ? "ملف التكاليف" : p._costSource === "retm" ? "تقرير المنتجات" : "غير معروف",
      ];
    });
    downloadCSV(`الهوامش_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  if (!hasProds) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 30 }} data-testid="margins-empty">
        <div style={{ fontSize: 40, marginBottom: 8 }}>💰</div>
        <h3 style={{ margin: 0, color: "var(--brown-deep)" }}>هامش الربح الحقيقي</h3>
        <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.9, marginTop: 8 }}>
          هذا التبويب يحلّل هامش الربح لكل منتج عبر دمج <b>تقرير المنتجات</b> + <b>ملف التكاليف</b> + <b>المشتريات</b>.
          ارفع تقرير المنتجات من رتم أولاً لبدء التحليل.
        </p>
        {canUpload && (
          <button type="button" className="btn btn-success" onClick={onUploadProds} data-testid="margins-upload-prods" style={{ marginTop: 14 }}>
            📦 رفع تقرير المنتجات
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="margins-tab">
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: "var(--brown-deep)" }}>💰 هامش الربح الحقيقي</h3>
        <span style={{ fontSize: 11.5, color: "var(--muted)" }}>{prods.length.toLocaleString("en-US")} منتج · {validMargins.length.toLocaleString("en-US")} بتكلفة معروفة</span>
        <button type="button" className="btn btn-soft" onClick={exportMarginsCsv} style={{ marginInlineStart: "auto" }} data-testid="margins-export">⬇ تصدير CSV</button>
      </div>

      {/* Cost source diagnostic banner */}
      <div className="al alo" data-testid="cost-source-banner" style={{ marginBottom: 8, fontSize: 12.5, lineHeight: 1.8 }}>
        <b>📐 مصادر التكلفة:</b>{" "}
        {stats.costFromFile > 0 && <>✅ <b>{stats.costFromFile}</b> من ملف التكاليف (الأدق) · </>}
        {stats.costFromRetm > 0 && <>📊 <b>{stats.costFromRetm}</b> مستنتجة من ربح رتم · </>}
        {stats.costMissing > 0 && <>⚠ <b>{stats.costMissing}</b> بدون تكلفة (مستثناة من الحساب)</>}
        {!costs?.length && (
          <div style={{ marginTop: 4, fontSize: 11, color: "var(--brown)" }}>
            💡 لدقة أعلى:
            {canUpload && <button type="button" className="btn btn-soft" onClick={onUploadCosts} style={{ marginInlineStart: 8, fontSize: 11, padding: "3px 10px" }} data-testid="margins-upload-costs">📤 ارفع ملف التكاليف</button>}
          </div>
        )}
      </div>

      {/* VAT explanation — appears for users using the cost file (the new
          canonical entry method since v117). Keeps the math transparent. */}
      {stats.costFromFile > 0 && (
        <div className="al" data-testid="vat-banner" style={{ marginBottom: 12, fontSize: 11.5, lineHeight: 1.8, background: "rgba(176,135,86,0.08)", border: "1px solid rgba(176,135,86,0.25)", padding: "8px 12px", borderRadius: 6 }}>
          🧾 <b>طريقة الحساب:</b> التكلفة في الملف <b>بدون ضريبة</b>، وسعر البيع <b>شامل ضريبة 15%</b>.
          الهامش يُحسب على الإيراد الصافي بعد خصم الضريبة:{" "}
          <code style={{ background: "rgba(0,0,0,0.05)", padding: "2px 6px", borderRadius: 4 }}>net = gross / 1.15</code>{" "}
          ثم <code style={{ background: "rgba(0,0,0,0.05)", padding: "2px 6px", borderRadius: 4 }}>margin = net − cost</code>.
        </div>
      )}

      <Kpi items={[
        { l: "إيراد POS (شامل الضريبة)", v: fm(stats.totalRevGross) + " ر", s: stats.vatOnSales > 0 ? `منها ${fm(stats.vatOnSales)} ر ضريبة` : "من تقرير المنتجات" },
        ...(stats.costFromFile > 0 ? [
          { l: "إيراد صافي (بعد خصم الضريبة)", v: fm(stats.totalRevBase) + " ر", s: "الأساس الفعلي للهامش" },
        ] : []),
        { l: "إجمالي تكلفة المنتجات", v: fm(stats.totalCost) + " ر", c: "o", s: "بدون ضريبة · حسب الكميات المباعة" },
        { l: "ربح المنتجات", v: fm(stats.totalProfit) + " ر", c: "g", s: "صافي بعد الضريبة − التكلفة" },
        { l: "هامش الربح %", v: stats.avgMarginPct.toFixed(1) + "%", c: stats.avgMarginPct >= 50 ? "g" : stats.avgMarginPct >= 25 ? "" : "o", s: "على الإيراد الصافي" },
        ...(stats.purchTotal > 0 ? [
          { l: "مصاريف الموردين (شامل ض)", v: fm(stats.purchTotal) + " ر", c: "r", s: `صافي ≈ ${fm(stats.purchNet)} ر` },
          { l: "صافي الربح الفعلي", v: fm(stats.realNet) + " ر", c: stats.realNet >= 0 ? "g" : "r", s: "ربح صافي − مشتريات صافية" },
        ] : []),
        { l: "منتجات خاسرة", v: stats.lossMakers.length.toLocaleString("en-US"), c: "r", s: stats.lossMakers.length > 0 ? "تحتاج مراجعة تسعير" : "✅" },
        { l: "نجوم", v: stats.stars.filter((p) => p.marginPct >= 50).length.toLocaleString("en-US"), c: "g", s: "هامش ≥ 50%" },
      ]} />

      {/* === AI Profit Margin Insights === */}
      <div className="card" data-testid="ai-profit-card" style={{ marginTop: 14, background: "linear-gradient(135deg, var(--beige) 0%, var(--card) 100%)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div>
            <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>🤖 تحليل ذكي لهامش الربح</h4>
            <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--muted)" }}>
              يدمج Claude بيانات المنتجات والتكاليف والمشتريات ويعطيك قرارات تسعيرية وتفاوضية محددة.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-success"
            disabled={aiBusy || !hasProds}
            onClick={runAi}
            data-testid="ai-profit-run"
          >
            {aiBusy ? "⏳ جارٍ التحليل..." : aiText ? "🔄 تحليل جديد" : "🚀 ابدأ التحليل"}
          </button>
        </div>
        {aiErr && (
          <div className="al alr" data-testid="ai-profit-error" style={{ marginTop: 10, fontSize: 12.5 }}>
            ⚠ {aiErr}
          </div>
        )}
        {aiText && (
          <div
            data-testid="ai-profit-output"
            style={{ marginTop: 12, padding: 14, background: "var(--card)", borderRadius: 10, border: "1px solid var(--border)", whiteSpace: "pre-wrap", lineHeight: 1.85, fontSize: 13.5, color: "var(--brown-deep)" }}
          >
            {aiText}
            {aiAt && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)", fontSize: 11, color: "var(--muted)" }}>
                🕒 {formatKSA(new Date(aiAt))}
              </div>
            )}
          </div>
        )}
        {!aiText && !aiErr && stats.costMissing === enriched.length && enriched.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
            💡 ملاحظة: لا تتوفر تكلفة لأي منتج. لتحليل أدق، ارفع ملف التكاليف أو تأكد أن تقرير المنتجات يحتوي عمود &quot;صافي الربح&quot;.
          </div>
        )}
      </div>

      <div className="g2" style={{ marginTop: 14 }}>
        <div className="card">
          <h4>🏆 أعلى 10 منتجات بهامش ربح</h4>
          <div className="cw" style={{ height: 360 }}><canvas id="c-marg-top"></canvas></div>
        </div>
        <div className="card">
          <h4>⚠ أقل 10 منتجات بهامش ربح</h4>
          <div className="cw" style={{ height: 360 }}><canvas id="c-marg-worst"></canvas></div>
        </div>
      </div>

      {/* Loss-makers table */}
      {stats.lossMakers.length > 0 && (
        <div className="card" style={{ marginTop: 14 }} data-testid="loss-makers">
          <h4>🚨 منتجات تخسر فيها ({stats.lossMakers.length})</h4>
          <div className="tw">
            <table>
              <thead>
                <tr><th>المنتج</th><th>الكمية</th><th>الإيراد الصافي</th><th>التكلفة</th><th>الخسارة</th><th>هامش %</th></tr>
              </thead>
              <tbody>
                {stats.lossMakers.slice(0, 25).map((p) => {
                  const baseRev = p._costSource === "file" ? p.netRev : p.rev;
                  return (
                  <tr key={p.name}>
                    <td><b>{p.name}</b></td>
                    <td>{fm(p.qty)}</td>
                    <td>{fm2(baseRev)} ر</td>
                    <td>{fm2(p.totalCost)} ر</td>
                    <td style={{ color: "var(--danger)" }}><b>{fm2(p.marginAbs)} ر</b></td>
                    <td><span className="b br_">{p.marginPct.toFixed(1)}%</span></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Full margin breakdown */}
      <div className="card" style={{ marginTop: 14 }} data-testid="all-margins">
        <h4>📋 تفصيل الهامش لجميع المنتجات</h4>
        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>
          الإيراد المعروض = <b>صافي بعد الضريبة</b> للصفوف من ملف التكاليف، وقيمة رتم الأصلية للباقي.
        </div>
        <div className="tw" style={{ maxHeight: 500, overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>#</th><th>المنتج</th><th>الكمية</th><th>الإيراد</th>
                <th>تكلفة/وحدة</th><th>إجمالي التكلفة</th><th>هامش</th><th>هامش %</th>
                <th>المصدر</th>
              </tr>
            </thead>
            <tbody>
              {[...enriched].sort((a, b) => b.rev - a.rev).slice(0, 60).map((p, i) => {
                const baseRev = p._costSource === "file" ? p.netRev : p.rev;
                return (
                <tr key={p.name + i} style={p._costSource === "none" ? { opacity: 0.5 } : null}>
                  <td>{i + 1}</td>
                  <td><b>{p.name}</b></td>
                  <td>{fm(p.qty)}</td>
                  <td>{fm2(baseRev)}</td>
                  <td>{p._costSource === "none" ? "—" : fm2(p.costPerUnit)}</td>
                  <td>{p._costSource === "none" ? "—" : fm2(p.totalCost)}</td>
                  <td style={{ color: p.marginAbs < 0 ? "var(--danger)" : "var(--success)" }}>
                    {p._costSource === "none" ? "—" : <b>{fm2(p.marginAbs)}</b>}
                  </td>
                  <td>{p._costSource === "none"
                    ? "—"
                    : <span className={`b ${p.marginPct >= 50 ? "bg" : p.marginPct >= 25 ? "bo" : "br_"}`}>{p.marginPct.toFixed(1)}%</span>
                  }</td>
                  <td style={{ fontSize: 11, color: "var(--muted)" }}>
                    {p._costSource === "file" ? "✅ ملف التكاليف" : p._costSource === "retm" ? "📊 رتم" : "⚠ مفقود"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
